/**
 * API-key sign-in (build spec, "Sign-in and roles"): the user pastes their encoded zInspector
 * key, the CRM checks it with one read route, the user picks themselves from the team list so
 * new deals get the right owner, and sign-out forgets the key. zInspector's own user
 * permissions are the roles: nothing role-related is stored in the CRM.
 */
import type { AuthProvider } from "ra-core";

import { canAccess } from "../commons/canAccess";
import {
  clearSession,
  getSession,
  saveSession,
  ziFetch,
  type ZiUser,
} from "./client";

/**
 * Resources with their own create/edit pages that the API key cannot write: those pages are
 * denied. Tasks, notes and tags are left alone because Atomic CRM embeds their create forms in
 * other pages (dashboard, contact and deal pages), and a denied form sends the whole page to
 * "access denied"; their writes fail with a readable error instead (see dataProvider.ts).
 */
const READ_ONLY_RESOURCES = new Set([
  "companies",
  "contacts",
  "sales",
  "leases",
]);
const WRITE_ACTIONS = new Set(["create", "edit", "delete", "clone"]);
/** The "New account" page only previews its requests on the live API, so it stays reachable. */
const ALWAYS_ALLOWED = new Set(["companies:create"]);
/** Atomic CRM pages zCRM has no use for: settings live in crmConfig.ts, data comes from zInspector. */
const HIDDEN_RESOURCES = new Set(["configuration", "import", "changelog"]);

/** zCRM access on top of Atomic CRM's role rules. Lists are never denied. */
export const zcrmCanAccess = (
  role: string,
  params: { action: string; resource: string },
): boolean => {
  const resource = params.resource.replace(/_summary$/, "");
  if (HIDDEN_RESOURCES.has(resource)) return false;
  if (ALWAYS_ALLOWED.has(`${resource}:${params.action}`)) {
    return canAccess(role, params);
  }
  if (WRITE_ACTIONS.has(params.action) && READ_ONLY_RESOURCES.has(resource)) {
    return false;
  }
  return canAccess(role, params);
};

export interface ZinspectorLoginParams {
  apiKey: string;
  userId: number;
  remember?: boolean;
}

/** Checks a key by reading the team list with it; resolves with the users the key can see. */
export const fetchUsersWithKey = async (apiKey: string): Promise<ZiUser[]> => {
  const response = await ziFetch<ZiUser[] | { results: ZiUser[] }>("/user/", {
    apiKey: apiKey.trim(),
  });
  return Array.isArray(response) ? response : (response.results ?? []);
};

export const zinspectorAuthProvider: AuthProvider = {
  async login({ apiKey, userId, remember }: ZinspectorLoginParams) {
    const users = await fetchUsersWithKey(apiKey);
    const user = users.find((u) => u.id === Number(userId));
    if (!user) throw new Error("Pick yourself from the zInspector team list.");
    saveSession({ apiKey: apiKey.trim(), user }, !!remember);
  },

  async logout() {
    clearSession();
  },

  async checkAuth() {
    if (!getSession()) throw new Error("Not signed in");
  },

  async checkError(error: { status?: number }) {
    // 401: the key is gone or was revoked. A 403 is usually the whitelist or a permission on one
    // record, so it is shown as an error without signing the user out.
    if (error?.status === 401) {
      clearSession();
      throw error;
    }
  },

  async getIdentity() {
    const user = getSession()?.user;
    if (!user) throw new Error("Not signed in");
    return {
      id: user.id,
      fullName:
        `${user.FirstName ?? ""} ${user.LastName ?? ""}`.trim() ||
        user.email ||
        `User ${user.id}`,
    };
  },

  async canAccess({ signal: _signal, ...params }) {
    const user = getSession()?.user;
    if (!user) return false;
    return zcrmCanAccess(user.role === "Admin" ? "admin" : "user", params);
  },
};
